import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { ImageNotSupported } from '@mui/icons-material';

const EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];

export const MEDIA_BASE_URL =
  'https://media.viskanassets.com/v2/enghssportprod/original/';

interface SmartImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  /** Explicit base URL (use together with imageName) */
  baseUrl?: string;
  /** Media name without extension (use together with baseUrl) */
  imageName?: string;
  /** Full URL or media name – alternative to baseUrl+imageName */
  src?: string | null;
  /** Size of the fallback icon when all extensions fail */
  fallbackIconSize?: 'small' | 'medium' | 'large';
}

/**
 * Strip any known image extension from a media name.
 * e.g. "F13004-9A_00932_01.png" → "F13004-9A_00932_01"
 */
const stripExtension = (name: string): string => {
  const lower = name.toLowerCase();
  for (const ext of EXTENSIONS) {
    if (lower.endsWith(ext)) {
      return name.slice(0, -ext.length);
    }
  }
  return name;
};

/**
 * Parse a full URL or bare media name into { baseUrl, imageName }.
 * Full URL:  "https://…/original/F13004-9A_00932_01.png"
 *            → baseUrl = "https://…/original/", imageName = "F13004-9A_00932_01"
 * Media name: "F13004-9A_00932_01.png"
 *            → baseUrl = MEDIA_BASE_URL, imageName = "F13004-9A_00932_01"
 */
const parseImageSource = (
  value: string,
): { baseUrl: string; imageName: string } => {
  const lastSlash = value.lastIndexOf('/');
  if (lastSlash >= 0 && value.includes('://')) {
    return {
      baseUrl: value.slice(0, lastSlash + 1),
      imageName: stripExtension(value.slice(lastSlash + 1)),
    };
  }
  return { baseUrl: MEDIA_BASE_URL, imageName: stripExtension(value) };
};

/**
 * SmartImage – tries multiple image extensions (.png → .jpg → .jpeg → .webp).
 * Shows an ImageNotSupported icon when all attempts fail or no source is given.
 *
 * Usage:
 *   <SmartImage baseUrl="https://…/original/" imageName="F13004-9A_00932_01" />
 *   <SmartImage src="https://…/original/F13004-9A_00932_01.png" />
 *   <SmartImage src="F13004-9A_00932_01" />
 */
const SmartImage: React.FC<SmartImageProps> = ({
  baseUrl: baseUrlProp,
  imageName: imageNameProp,
  src,
  fallbackIconSize = 'medium',
  ...props
}) => {
  const resolved = useMemo(() => {
    if (baseUrlProp && imageNameProp !== undefined) {
      return {
        baseUrl: baseUrlProp,
        imageName: stripExtension(imageNameProp),
      };
    }
    if (src) {
      return parseImageSource(src);
    }
    return null;
  }, [baseUrlProp, imageNameProp, src]);

  const [extIndex, setExtIndex] = useState(0);
  const [allFailed, setAllFailed] = useState(false);

  // Reset state when the source changes
  useEffect(() => {
    setExtIndex(0);
    setAllFailed(false);
  }, [resolved?.baseUrl, resolved?.imageName]);

  const handleError = () => {
    const nextIndex = extIndex + 1;
    if (nextIndex < EXTENSIONS.length) {
      setExtIndex(nextIndex);
    } else {
      setAllFailed(true);
    }
  };

  if (!resolved || allFailed) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          color: '#bbb',
        }}
      >
        <ImageNotSupported fontSize={fallbackIconSize} />
      </Box>
    );
  }

  return (
    <img
      src={`${resolved.baseUrl}${resolved.imageName}${EXTENSIONS[extIndex]}`}
      onError={handleError}
      loading="lazy"
      {...props}
    />
  );
};

export default SmartImage;
